import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CheckIcon from '@material-ui/icons/Check';
import fileDownload from 'js-file-download';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../../api/WindsorApi';
import UserContext from '../../../auth/UserContext';
import LinkRouter from '../../../common/LinkRouter';
import LoadingIndicator from '../../../common/LoadingIndicator';
import MiddleDot from '../../../common/MiddleDot';
import MobxContext from '../../../mobx/MobxContext';
import { ExecutionStatus } from '../../executions/ExecutionConstants';
import ExecutionStateParser from '../../executions/ExecutionStateParser';
import ExecutionStatusIcon from '../../executions/ExecutionStatusIcon';
import { ExecutionEventType } from '../../executions/ExecutionStore';
import WorkflowCloneDialog from './WorkflowCloneDialog';
import WorkflowDetailsTab from './WorkflowDetailsTab';
import WorkflowExecutionsTab from './WorkflowExecutionsTab';
import WorkflowReadonlyJsonTab from './WorkflowReadonlyJsonTab';
import WorkflowRevisionsTab from './WorkflowRevisionsTab';
import * as jsYaml from 'js-yaml';
import DownloadWorkflowDialog from './DownloadWorkflowDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    header: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between'
      }
    },
    titleContainer: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    subtitleTypography: {
      fontWeight: 500
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03),
      marginBottom: 24
    },
    actionButtonsContainer: {
      display: 'flex',
      alignItems: 'start'
    },
    buttonIcon: {
      marginLeft: 4
    },
    archivedChip: {
      marginLeft: 16
    },
    deprecatedChip: {
      marginLeft: 16
    },
    primaryTitleContainer: {
      display: 'flex'
    },
    secondaryActionButton: {
      marginRight: 16
    },
    menuItem: {
      justifyContent: 'space-between'
    },
    executionStatusPanel: {
      border: `1px solid ${theme.palette.divider}`,
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      marginBottom: 24
    },
    executionLink: {
      margin: '0 4px'
    },
    lastCompletedExecutionText: {
      marginLeft: 8
    }
  })
);

const windsorApi = new WindsorApi();

function downloadWorkflow(workflow: any, format: string) {
  const lastModifiedDate = moment.unix(parseInt(workflow.LastModified)).toISOString();

  const strToDownload = format === 'yaml' ? jsYaml.dump(workflow, {
    'sortKeys': true
  }) : JSON.stringify(workflow, null, 2);
  fileDownload(strToDownload, `${workflow.Name}-${lastModifiedDate}.${format === 'yaml' ? 'yml' : 'json'}`);
}

function WorkflowDetailsPage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match, history, location, enqueueSnackbar } = props;
  const familyId = match.params.familyId;
  const workflowId = match.params.workflowId;
  const { tab = 'details', version } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const classes = useStyles({});
  const [workflow, setWorkflow] = useState(null);
  const [executions, setExecutions] = useState(null);
  const [isLatest, setIsLatest] = useState(null);
  const [family, setFamily] = useState(null);
  const [actionsInProgress, setActionsInProgress] = useState({ subscribe: false, archive: false });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showCloneWorkflowDialog, setShowCloneWorkflowDialog] = useState(false);
  const [lastExecution, setLastExecution] = useState(null);
  const [lastExecutionState, setLastExecutionState] = useState(null);
  const [showDownloadWorkflowDialog, setShowDownloadWorkflowDialog] = useState(null);
  const user = useContext(UserContext);
  const { executionStore } = useContext(MobxContext);

  useEffect(() => {
    setWorkflow(null);
    setExecutions(null);
    setLastExecution(null);
    setLastExecutionState(null);

    Promise.all([
      windsorApi.getWorkflow(familyId, workflowId),
      version ? windsorApi.getWorkflow(familyId, workflowId, version) : null,
      windsorApi.getFamily(familyId)
    ]).then(([latestWorkflow, workflowRevision, family]) => {
      setWorkflow(version ? workflowRevision : latestWorkflow);
      setIsLatest(!version || latestWorkflow.LastModified === workflowRevision.LastModified);
      setFamily(family);
    });
  }, [familyId, workflowId, version]);

  useEffect(() => {
    if (workflow && workflow.LastExecutionGUID && workflow.LastExecutionGUID !== '0') {
      // Load last execution
      windsorApi.getExecution(familyId, workflow.LastExecutionGUID).then(execution => {
        setLastExecution(execution);
        setLastExecutionState(new ExecutionStateParser().parseState(execution));
      });
    }
  }, [familyId, workflow]);

  useEffect(() => {
    if (tab === 'executions' && !executions && isLatest) {
      executionStore.getWorkflowExecutions(familyId, workflowId).then(response => {
        setExecutions(response);
      });
    }
  }, [tab, executions, familyId, workflowId, executionStore, isLatest]);

  useEffect(() => {
    if (isLatest) {
      return executionStore.addExecutionListener((eventType: ExecutionEventType, data: any) => {
        if (!executions) {
          // Reload executions
          executionStore.getWorkflowExecutions(familyId, workflowId).then(response => {
            setExecutions(response);
          });
        } else {
          if (eventType === ExecutionEventType.ExecutionCreated) {
            if (!executions.some(execution => execution.id === data.id)) {
              setExecutions([...executions, data]);
            }
          } else if (eventType === ExecutionEventType.ExecutionUpdated) {
            const index = executions.findIndex(execution => execution.id === data.id);
            if (index !== -1) {
              const executionsCopy = executions.slice();
              executionsCopy[index] = data;
              setExecutions(executionsCopy);
            }
          }
        }

        if (data.executionStatus === ExecutionStatus.Passed ||
          data.executionStatus === ExecutionStatus.PassedWithWarnings ||
          data.executionStatus === ExecutionStatus.Failed) {
          // Reload workflow
          windsorApi.getWorkflow(familyId, workflowId).then(res => setWorkflow(res));
        }
      });
    }
  }, [executionStore, executions, familyId, workflowId, isLatest]);

  const onToggleSubscription = () => {
    setActionsInProgress({ ...actionsInProgress, subscribe: true });
    onMenuClose();

    const subscribe = !workflow.Subscribers || !workflow.Subscribers.includes(user.email);
    windsorApi.updateWorkflowSubscription(subscribe, workflowId, familyId)
      .then(() => windsorApi.getWorkflow(familyId, workflowId))
      .then(workflow => {
        setActionsInProgress({ ...actionsInProgress, subscribe: false });
        setWorkflow(workflow);
        enqueueSnackbar(subscribe ? 'Subscription added' : 'Subscription removed');
      });
  }

  const onToggleArchiveState = () => {
    setActionsInProgress({ ...actionsInProgress, archive: true });
    onMenuClose();

    const archive = !workflow.Archived;
    const promise = archive ? windsorApi.archiveWorkflow(workflowId, familyId) :
      windsorApi.unarchiveWorkflow(workflowId, familyId);
    promise.then(() => windsorApi.getWorkflow(familyId, workflowId))
      .then(workflow => {
        setActionsInProgress({ ...actionsInProgress, archive: false });
        setWorkflow(workflow);
        enqueueSnackbar(archive ? 'Workfow archived' : 'Workflow unarchived');
      });
  }

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push({ search: `?tab=${newValue}${version ? `&version=${version}` : ''}` });
  }

  const onExecute = () => {
    history.push(`/families/${familyId}/workflows/${workflowId}/execute`);
  }

  const onClone = (newWorkflowFamilyId: string) => {
    history.push(`/families/${newWorkflowFamilyId}/workflows/new?cloneFrom=${encodeURIComponent(JSON.stringify({ familyId, workflowId }))}`);
  };

  const onEdit = () => {
    history.push(`/families/${familyId}/workflows/${workflowId}/edit`);
  }

  const onMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const onShowCloneWorkflowDialog = () => {
    onMenuClose();
    setShowCloneWorkflowDialog(true);
  }

  const onCloseCloneWorkflowDialog = () => {
    setShowCloneWorkflowDialog(false);
  }

  const onDownload = () => {
    onMenuClose();
    setShowDownloadWorkflowDialog(true);
  }

  const onCloseDownloadWorkflowDialog = (fileType?: string) => {
    setShowDownloadWorkflowDialog(false);
    if (fileType) {
      downloadWorkflow(workflow, fileType);
    }
  };

  if (workflow && family && (!workflow.LastExecutionGUID || workflow.LastExecutionGUID === '0' || (lastExecutionState && lastExecution))) {
    const subscribed = !!workflow.Subscribers && workflow.Subscribers.includes(user.email);

    return (
      <div className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb">
          <LinkRouter color="textSecondary" to={`/families`}>
            Families
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
            {family.Name}
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
            Workflows
          </LinkRouter>
          <Typography color="textPrimary">{workflow.Name}</Typography>
        </Breadcrumbs>
        <Divider className={classes.divider} />
        <div className={classes.header}>
          <div className={classes.titleContainer}>
            <div className={classes.primaryTitleContainer}>
              <Typography variant="h6" gutterBottom>Workflow: {workflow.Name}</Typography>
              {workflow.Archived && <Chip className={classes.archivedChip} label="Archived" color="secondary" />}
              {!isLatest && <Chip className={classes.deprecatedChip} label="Inactive Version" color="secondary" />}
            </div>
            <div>
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Family: {family.Name}
              </Typography>
              <MiddleDot />
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Environment: {workflow.AccountName}
              </Typography>
              <MiddleDot />
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Region: {workflow.Region}
              </Typography>
              <MiddleDot />
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Last Updated: {moment.unix(parseInt(workflow.LastModified)).fromNow()}
              </Typography>
            </div>
          </div>
          {isLatest && <div className={classes.actionButtonsContainer}>
            <Button
              color="primary"
              variant="outlined"
              onClick={onExecute}
              className={classes.secondaryActionButton}>
              Execute
            </Button>
            <ButtonGroup variant="contained" color="primary" aria-label="split button">
              <Button onClick={onEdit}>Edit Workflow</Button>
              <Button
                color="primary"
                size="small"
                aria-label="more options menu"
                aria-haspopup="menu"
                onClick={onOpenMenu}
              >
                {(actionsInProgress.subscribe || actionsInProgress.archive) && <CircularProgress color="secondary" size={16} />}
                {!actionsInProgress.subscribe && !actionsInProgress.archive && <ArrowDropDownIcon fontSize="small" />}
              </Button>
            </ButtonGroup>
            <Menu
              id="more-options-menu"
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={onMenuClose}
            >
              <MenuItem onClick={onToggleSubscription} className={classes.menuItem}>
                {subscribed ? 'Subscribed' : 'Subscribe'}
                {subscribed && <CheckIcon fontSize="small" className={classes.buttonIcon} />}
              </MenuItem>
              <MenuItem onClick={onToggleArchiveState} className={classes.menuItem}>
                {workflow.Archived ? 'Archived' : 'Archive'}
                {workflow.Archived && <CheckIcon fontSize="small" className={classes.buttonIcon} />}
              </MenuItem>
              <MenuItem onClick={onShowCloneWorkflowDialog} className={classes.menuItem}>
                Clone
              </MenuItem>
              <MenuItem onClick={onDownload} className={classes.menuItem}>
                Download
              </MenuItem>
            </Menu>
          </div>}
        </div>
        {lastExecutionState && <div className={classes.executionStatusPanel}>
          <ExecutionStatusIcon type={lastExecutionState.status} />
          <Typography className={classes.lastCompletedExecutionText} variant="subtitle1">
            Last completed
          </Typography>
          <LinkRouter variant="body1" className={classes.executionLink} to={`/families/${familyId}/executions/${lastExecution.GUID}`}>
            execution
           </LinkRouter>
          <Typography variant="subtitle1">
            was {moment.unix(parseInt(workflow.LastExecutionTime)).fromNow()}
          </Typography>
        </div>}
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={onTabChange}
          aria-label="Workflow Details Tabs"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Details" value="details" />
          {isLatest && <Tab label="Revisions" value="revisions" />}
          {isLatest && <Tab label="Executions" value="executions" />}
          <Tab label="JSON" value="json" />
        </Tabs>
        {tab === 'details' && <WorkflowDetailsTab workflow={workflow} family={family} />}
        {tab === 'revisions' && <WorkflowRevisionsTab workflow={workflow} />}
        {tab === 'executions' && <WorkflowExecutionsTab familyId={familyId} executions={executions} />}
        {tab === 'json' && <WorkflowReadonlyJsonTab workflow={workflow} />}
        {showCloneWorkflowDialog && <WorkflowCloneDialog
          onClose={onCloseCloneWorkflowDialog}
          onSubmit={onClone}
          defaultFamilyId={familyId}
          defaultFamilyName={family.Name}
        />}
        {showDownloadWorkflowDialog && <DownloadWorkflowDialog onClose={onCloseDownloadWorkflowDialog} />}
      </div>
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }
};

export default withRouter(withSnackbar(observer(WorkflowDetailsPage)));
